import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { UnitPreferenceProvider } from "@/contexts/UnitPreferenceContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Shell from "@/components/Shell";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";import MarketingHome from "@/pages/MarketingHome";
import Portfolio from "@/pages/Portfolio";
import OperationsDashboard from "@/pages/OperationsDashboard";
import Project from "@/pages/Project";
import IngestionReview from "@/pages/IngestionReview";
// Iter 109 P0 — Both `ProjectDataIntake` and `PlansBrowser` retired as
// stand-alone surfaces. Routes redirect to canonical pages.

function LegacyIntakeRedirect() {
  const { projectId } = useParams();
  return <Navigate to={`/intake?project=${projectId}`} replace />;
}
function LegacyPlansRedirect() {
  const { projectId } = useParams();
  return <Navigate to={`/project/${projectId}`} replace />;
}

// Iter 124 P0 — Plan-first refocus. Authenticated visitors hitting `/`
// land in their portfolio; anonymous visitors still see the marketing
// homepage. Uses the existing AuthContext so we don't introduce a new
// dependency or collection.
function AuthedHome() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/portfolio" replace /> : <MarketingHome />;
}
import SheetViewer from "@/pages/SheetViewer";
import ViewerErrorBoundary from "@/components/ViewerErrorBoundary";
import PlanSharePage from "@/pages/PlanSharePage";
import RevisionCompare from "@/pages/RevisionCompare";
import RevisionWarRoom from "@/pages/RevisionWarRoom";
import ForemanInbox from "@/pages/ForemanInbox";
import SuperintendentCommandView from "@/pages/SuperintendentCommandView";
import DemoAdmin from "@/pages/DemoAdmin";
import DigestPreview from "@/pages/DigestPreview";
import FieldTimeline from "@/pages/FieldTimeline";
import FieldRoll from "@/pages/FieldRoll";
import QuantityIntelligence from "@/pages/QuantityIntelligence";
import MySheets from "@/pages/MySheets";
import ProjectRevisions from "@/pages/ProjectRevisions";
import ProjectPackages from "@/pages/ProjectPackages";
import PackageSharePortal from "@/pages/PackageSharePortal";
import PilotDiscoveries from "@/pages/PilotDiscoveries";
import IntakeCenter from "@/pages/IntakeCenter";
import IssuesDashboard from "@/pages/IssuesDashboard";
import Executive from "@/pages/Executive"; // retired Iter 59.2 — kept for backward URL compatibility (renders 410 banner)
import ActivityPage from "@/pages/Activity";
import WorkflowDetail from "@/pages/WorkflowDetail";
import TenantSettings from "@/pages/TenantSettings";
import TenantAdmin from "@/pages/TenantAdmin";
import PlatformAdmin from "@/pages/PlatformAdmin";
import PlatformConsole from "@/pages/PlatformConsole";
import CustomerDetail from "@/pages/CustomerDetail";
import TenantCommandCenter from "@/pages/TenantCommandCenter";
import UserManagementPage from "@/pages/UserManagementPage";
import AcceptInvite from "@/pages/AcceptInvite";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SharePortal from "@/pages/SharePortal";
import GlobalInbox from "@/pages/GlobalInbox";
import PunchCenter from "@/pages/PunchCenter";
import PunchDetail from "@/pages/PunchDetail";
import ExportsList from "@/pages/ExportsList";
import SharesList from "@/pages/SharesList";
import SpecSectionDetail from "@/pages/SpecSectionDetail";
import AskPage from "@/pages/AskPage";
import KnowledgeRecordsList from "@/pages/KnowledgeRecordsList";
import KnowledgeRecordDetail from "@/pages/KnowledgeRecordDetail";
import PublicKnowledgeShare from "@/pages/PublicKnowledgeShare";
import OperationsCommandCenter from "@/pages/OperationsCommandCenter";
import CloseoutCenter from "@/pages/CloseoutCenter";
import SurveyControl from "@/pages/SurveyControl";
import CalibrationStudio from "@/pages/CalibrationStudio";
import TermsOfUse from "@/pages/legal/TermsOfUse";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import SecurityPage from "@/pages/legal/Security";
import ContactPage from "@/pages/legal/Contact";
import SupportPage from "@/pages/legal/Support";
import HelpPage from "@/pages/legal/Help";
import TrustCenter from "@/pages/legal/TrustCenter";
import CommandPalette from "@/components/search/CommandPalette";
import ScrollToTop from "@/components/ScrollToTop";
import { Toaster } from "@/components/ui/sonner";
import SyncSuccessListener from "@/components/SyncSuccessListener";
import { api } from "@/lib/api";
import { setSyncClient } from "@/lib/syncQueue";
import { drainAll } from "@/lib/syncTruth";
import { runV1MigrationOnce } from "@/lib/offlineQueueV1Migration";

// Iter 36 — boot the sync queue with the axios client so queued
// mutations can drain whenever connectivity returns. Done at module
// scope (not inside App) so it runs exactly once and is ready before
// any component mounts that might enqueue.
setSyncClient(api);

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Founder Commercial Amendment (2026-09-07) — public self-service
          Starter signup, no auth, no Shell. */}
      <Route path="/signup" element={<Signup />} />
      {/* Iter 55 — public accept-invite page (no auth, no Shell) */}
      <Route path="/accept-invite" element={<AcceptInvite />} />
      {/* FP-011 (2026-09-09 audit remediation) — public forgot/reset
          password flow, no auth, no Shell. */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Public branded share preview portal — no auth, no Shell */}
      <Route path="/r/:token" element={<SharePortal />} />
      {/* Iter 76 — Secure Plan Share public viewer — no auth, no Shell */}
      <Route path="/plan-share/:token" element={<PlanSharePage />} />
      {/* Iter 124 P0 — Authed users hitting `/` should land on their
          portfolio, not the marketing site. Anonymous traffic still
          sees the marketing page. */}
      <Route path="/" element={<AuthedHome />} />
      {/* Public legal, security, and contact pages. No auth, no Shell. */}
      <Route path="/legal/terms" element={<TermsOfUse />} />
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
      <Route path="/legal/security" element={<SecurityPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/trust" element={<TrustCenter />} />
      {/* Iter 93 — public Sheet-Package share portal. No auth, no Shell. */}
      <Route path="/share/:token" element={<PackageSharePortal />} />
      <Route
        path="/project/:projectId/revisions"
        element={<ProtectedRoute><Shell><ProjectRevisions /></Shell></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId/packages"
        element={<ProtectedRoute><Shell><ProjectPackages /></Shell></ProtectedRoute>}
      />
      <Route
        path="/discoveries"
        element={<ProtectedRoute><Shell><PilotDiscoveries /></Shell></ProtectedRoute>}
      />
      <Route
        path="/intake"
        element={<ProtectedRoute><Shell><IntakeCenter /></Shell></ProtectedRoute>}
      />
      <Route
        path="/portfolio"
        element={<ProtectedRoute><Shell><Portfolio /></Shell></ProtectedRoute>}
      />
      <Route
        path="/operations"
        element={<ProtectedRoute><Shell><OperationsDashboard /></Shell></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId"
        element={<ProtectedRoute><Shell><Project /></Shell></ProtectedRoute>}
      />
      {/* Iter 109 P0 — Legacy `PlansBrowser` and `ProjectDataIntake` retired.
          PlansBrowser dead code removed. ProjectDataIntake replaced by an
          inline redirect to the unified IntakeCenter (`/intake?project=...`).
          One document path. One source of truth. */}
      <Route
        path="/project/:projectId/plans"
        element={<ProtectedRoute><LegacyPlansRedirect /></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId/ingest/:batchId"
        element={<ProtectedRoute><Shell><IngestionReview /></Shell></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId/documents/intake"
        element={<ProtectedRoute><LegacyIntakeRedirect /></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId/data-intake"
        element={<ProtectedRoute><LegacyIntakeRedirect /></ProtectedRoute>}
      />
      <Route
        path="/sheet/:sheetId"
        element={<ProtectedRoute><ViewerErrorBoundary><SheetViewer /></ViewerErrorBoundary></ProtectedRoute>}
      />
      <Route
        path="/sheet/:sheetId/compare"
        element={<ProtectedRoute><RevisionCompare /></ProtectedRoute>}
      />
      <Route
        path="/projects/:projectId/war-room"
        element={<ProtectedRoute><RevisionWarRoom /></ProtectedRoute>}
      />
      <Route
        path="/inbox"
        element={<ProtectedRoute><ForemanInbox /></ProtectedRoute>}
      />
      <Route
        path="/command"
        element={<ProtectedRoute><SuperintendentCommandView /></ProtectedRoute>}
      />
      <Route
        path="/admin/demo"
        element={<ProtectedRoute><Shell><DemoAdmin /></Shell></ProtectedRoute>}
      />
      <Route
        path="/digest"
        element={<ProtectedRoute><Shell><DigestPreview /></Shell></ProtectedRoute>}
      />
      <Route
        path="/sheets/:sheetId/field-timeline"
        element={<ProtectedRoute><Shell><FieldTimeline /></Shell></ProtectedRoute>}
      />
      {/* Track 3 — Foreman Field Roll. Mobile-first single field surface. */}
      <Route
        path="/field-roll"
        element={<ProtectedRoute><FieldRoll /></ProtectedRoute>}
      />
      <Route
        path="/quantity-intelligence"
        element={<ProtectedRoute><Shell><QuantityIntelligence /></Shell></ProtectedRoute>}
      />
      {/* Wave 6 (2026-09-11) — Favorites / My Sheets */}
      <Route
        path="/my-sheets"
        element={<ProtectedRoute><MySheets /></ProtectedRoute>}
      />
      {/* Iter 116 P1 — Spec section destination page. */}
      <Route
        path="/spec-section/:sectionId"
        element={<ProtectedRoute><Shell><SpecSectionDetail /></Shell></ProtectedRoute>}
      />
      {/* Iter 119 — ForgedOps Ask V1 verified knowledge engine. */}
      <Route
        path="/ask"
        element={<ProtectedRoute><Shell><AskPage /></Shell></ProtectedRoute>}
      />
      {/* Iter 120 — Project Knowledge Records (operationalized Ask). */}
      <Route
        path="/knowledge"
        element={<ProtectedRoute><Shell><KnowledgeRecordsList /></Shell></ProtectedRoute>}
      />
      <Route
        path="/knowledge/:recordId"
        element={<ProtectedRoute><Shell><KnowledgeRecordDetail /></Shell></ProtectedRoute>}
      />
      {/* Iter 120 — Public read-only share of a verified knowledge record. */}
      <Route
        path="/k-share/:token"
        element={<PublicKnowledgeShare />}
      />
      <Route
        path="/workflow/:workflowId"
        element={<ProtectedRoute><Shell><WorkflowDetail /></Shell></ProtectedRoute>}
      />
      <Route
        path="/issues"
        element={<ProtectedRoute><Shell><IssuesDashboard /></Shell></ProtectedRoute>}
      />
      <Route
        path="/executive"
        element={<ProtectedRoute><Shell><Executive /></Shell></ProtectedRoute>}
      />
      <Route
        path="/activity"
        element={<ProtectedRoute><Shell><ActivityPage /></Shell></ProtectedRoute>}
      />
      <Route
        path="/settings"
        element={<ProtectedRoute><Shell><TenantSettings /></Shell></ProtectedRoute>}
      />
      {/* Iter 55 — tenant administration (owner/admin) */}
      <Route
        path="/admin/users"
        element={<ProtectedRoute><Shell><TenantAdmin /></Shell></ProtectedRoute>}
      />
      {/* Iter 55 — platform administration (forgedops operators) */}
      <Route
        path="/platform"
        element={<ProtectedRoute><Shell><PlatformConsole /></Shell></ProtectedRoute>}
      />
      <Route
        path="/platform/legacy"
        element={<ProtectedRoute><Shell><PlatformAdmin /></Shell></ProtectedRoute>}
      />
      {/* Iter 71.0 — Tenant Command Center (per-tenant detail page) */}
      <Route
        path="/platform/tenants/:tenantId"
        element={<ProtectedRoute><Shell><CustomerDetail /></Shell></ProtectedRoute>}
      />
      {/* Iter 71.4 — User Management Command Center (cross-tenant) */}
      <Route
        path="/platform/users"
        element={<ProtectedRoute><Shell><UserManagementPage /></Shell></ProtectedRoute>}
      />
      {/* RC2 Sprint 0 — Reachability surfaces (no new functionality;
            wraps existing API endpoints in discoverable pages).
            Pillar 6 (foreman roll-call) owns /inbox; legacy surface is
            kept reachable at /inbox/global for backward links. */}
      <Route
        path="/inbox/global"
        element={<ProtectedRoute><Shell><GlobalInbox /></Shell></ProtectedRoute>}
      />
      <Route
        path="/punch"
        element={<ProtectedRoute><Shell><PunchCenter scope="global" /></Shell></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId/punch"
        element={<ProtectedRoute><Shell><PunchCenter scope="project" /></Shell></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId/punch/:punchId"
        element={<ProtectedRoute><Shell><PunchDetail /></Shell></ProtectedRoute>}
      />
      <Route
        path="/exports"
        element={<ProtectedRoute><Shell><ExportsList scope="global" /></Shell></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId/exports"
        element={<ProtectedRoute><Shell><ExportsList scope="project" /></Shell></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId/exports/:jobId"
        element={<ProtectedRoute><Shell><ExportsList scope="project" /></Shell></ProtectedRoute>}
      />
      <Route
        path="/shares"
        element={<ProtectedRoute><Shell><SharesList scope="global" /></Shell></ProtectedRoute>}
      />
      <Route
        path="/operations/legacy"
        element={<ProtectedRoute><Shell><OperationsCommandCenter /></Shell></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId/closeout"
        element={<ProtectedRoute><Shell><CloseoutCenter /></Shell></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId/survey-control"
        element={<ProtectedRoute><Shell><SurveyControl /></Shell></ProtectedRoute>}
      />
      <Route
        path="/projects/:projectId/survey-control"
        element={<ProtectedRoute><Shell><SurveyControl /></Shell></ProtectedRoute>}
      />
      <Route
        path="/sheet/:sheetId/calibrate"
        element={<ProtectedRoute><Shell><CalibrationStudio /></Shell></ProtectedRoute>}
      />
      <Route
        path="/projects/:projectId/closeout"
        element={<ProtectedRoute><Shell><CloseoutCenter /></Shell></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId/shares"
        element={<ProtectedRoute><Shell><SharesList scope="project" /></Shell></ProtectedRoute>}
      />
      <Route
        path="/project/:projectId/shares/:shareId"
        element={<ProtectedRoute><Shell><SharesList scope="project" /></Shell></ProtectedRoute>}
      />
      {/* RC2 Sprint 0 — Plural alias routes (defensive in case any
            historical notification rows in the DB still emit
            `/projects/...` deep_links). */}
      <Route path="/projects/:projectId" element={<ProtectedRoute><Shell><Project /></Shell></ProtectedRoute>} />
      <Route
        path="/projects/:projectId/punch"
        element={<ProtectedRoute><Shell><PunchCenter scope="project" /></Shell></ProtectedRoute>}
      />
      <Route
        path="/projects/:projectId/punch/:punchId"
        element={<ProtectedRoute><Shell><PunchDetail /></Shell></ProtectedRoute>}
      />
      <Route
        path="/projects/:projectId/exports"
        element={<ProtectedRoute><Shell><ExportsList scope="project" /></Shell></ProtectedRoute>}
      />
      <Route
        path="/projects/:projectId/exports/:jobId"
        element={<ProtectedRoute><Shell><ExportsList scope="project" /></Shell></ProtectedRoute>}
      />
      <Route
        path="/projects/:projectId/shares"
        element={<ProtectedRoute><Shell><SharesList scope="project" /></Shell></ProtectedRoute>}
      />
      <Route
        path="/projects/:projectId/shares/:shareId"
        element={<ProtectedRoute><Shell><SharesList scope="project" /></Shell></ProtectedRoute>}
      />
      <Route
        path="/projects/:projectId/sheets/:sheetId"
        element={<ProtectedRoute><ViewerErrorBoundary><SheetViewer /></ViewerErrorBoundary></ProtectedRoute>}
      />
      <Route
        path="/projects/:projectId/objects/:objectId"
        element={<ProtectedRoute><Shell><Project /></Shell></ProtectedRoute>}
      />
      <Route path="*" element={<Navigate to="/portfolio" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Register service worker for offline sheet/thumbnail caching.
    if ("serviceWorker" in navigator) {
      const onLoad = () => {
        navigator.serviceWorker
          .register("/service-worker.js", { scope: "/" })
          .catch(() => { /* fail silently — app still works without offline cache */ });
      };
      if (document.readyState === "complete") onLoad();
      else window.addEventListener("load", onLoad);
      return () => window.removeEventListener("load", onLoad);
    }
  }, []);

  // Iter 36 — opportunistic drain on app boot in case the user was
  // offline last session and now arrives with connectivity. Uses the
  // canonical syncTruth facade so both offline engines drain (v1's
  // separate pin queue was retired — see FP-034/038 migration below).
  useEffect(() => { drainAll(); }, []);

  // FP-038 (2026-09-09 audit remediation) — one-time, idempotent,
  // resumable migration of any legacy v1 offline-pin-queue entries
  // left behind on this device before the queue was retired. v1 had
  // no owner field, so any found entries are quarantined for an
  // explicit human decision (see OfflineQueuePanel) — never silently
  // assigned to whoever happens to be logged in when this runs.
  useEffect(() => { runV1MigrationOnce(); }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <TenantProvider>
          <UnitPreferenceProvider>
          <AppRouter />
          {/* Founder "One Sync Truth" + offline success feedback + stuck-
              pending safety net — mounted at root so it's active on every
              page including SheetViewer (which bypasses Shell). */}
          <SyncSuccessListener />
          {/* Iter 49 — global Cmd/Ctrl+K palette. Outside the routes so it
              works on every page including SheetViewer. Renders nothing
              until opened. */}
          <CommandPalette />
          {/* Iter 70.1 — global sonner Toaster host so toast.* notifications
              actually render (Lifecycle "Tick complete", comms test-send,
              feedback submit, marketing lead actions, etc.). */}
          <Toaster richColors position="top-right" closeButton />
          </UnitPreferenceProvider>
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
